import { warn } from '@pressreader/logger';

import { QUERY_SEPARATOR, readQueryValue, splitByLast } from './routing.utils';

/**
 * A query parameter carried by every route, whether or not a route template declares it.
 * @param name Canonical name. The name the parameter is read and written under.
 * @param aliases Alternative names accepted while resolving a url and while reading generated url data.
 */
export interface GlobalQueryParam {
    name: string;
    aliases?: string[];
}

/**
 * Canonical name -> aliases, in registration order.
 * The registry describes the url contract of the application rather than of a single router
 * instance, so every router produced by `createRouter` reads the same set.
 */
const globalQueryParams = new Map<string, Set<string>>();

/**
 * Register query parameters resolved and generated for every route.
 * Registering a name already known merges the given aliases into the known ones.
 * @param params Parameters to register.
 */
export function registerGlobalQueryParams(params: GlobalQueryParam[]) {
    params.forEach(({ name, aliases = [] }) => {
        if (!name) {
            warn('routing: a global query parameter without a name is ignored');
            return;
        }

        const known = globalQueryParams.get(name) ?? new Set<string>();
        aliases.forEach(alias => {
            if (alias && alias !== name) {
                known.add(alias);
            }
        });
        globalQueryParams.set(name, known);
    });
}

/**
 * Test seam. Drops every registered parameter.
 */
export function __resetGlobalQueryParamsForTests() {
    globalQueryParams.clear();
}

/**
 * Merge the registered parameters of a resolved path into route data, under their canonical names.
 * A name the route resolved itself keeps the route's value, and a parameter the route resolved under
 * one of its aliases is left to the route: its canonical name is then read from the query only when
 * the query names it, never copied from the alias. The others are read the way a declared parameter
 * is read (`readQueryValue`): the canonical name first, then the aliases in registration order.
 * A name registered both as a canonical name and as another parameter's alias feeds both.
 * @param routeData Route data to extend in place.
 * @param path Path (with query) the route was resolved from.
 */
export function mergeGlobalQueryParams(routeData: Record<string, unknown>, path: string) {
    if (!globalQueryParams.size) {
        return;
    }

    // What the route resolved on its own, taken before the registry writes anything: a name another
    // registered parameter fed just now is the registry's, not the route's.
    const resolvedByRoute = new Set(Object.keys(routeData).filter(name => routeData[name] !== undefined));

    const [, queryStr] = splitByLast(path, QUERY_SEPARATOR);
    globalQueryParams.forEach((aliases, canonical) => {
        if (resolvedByRoute.has(canonical)) {
            return;
        }

        // Copying an alias the route resolved under its canonical name would give one value two names
        // in the data, and a writer clearing the alias — the legacy popup close does — would leave the
        // canonical behind on the next url.
        const aliasResolved = Array.from(aliases).some(alias => resolvedByRoute.has(alias));
        const value = readQueryValue(queryStr, aliasResolved ? [canonical] : [canonical, ...aliases]);
        if (value !== undefined) {
            routeData[canonical] = value;
        }
    });
}

/**
 * Value given for a name, as `Route.generateUrl` casts it; `undefined` when none is given, an empty
 * value counting as none.
 */
function readGivenValue(givenValues: Readonly<Record<string, string>>, name: string) {
    const value = givenValues[name];

    // An absent name reads as `undefined` and passes through.
    return value === '' ? undefined : value;
}

/**
 * The value to emit for a registered parameter, or `undefined` when the route needs nothing added.
 *
 * A query already carrying the canonical name is left alone, whatever its value. Otherwise the
 * canonical value given in the data is emitted even when the route serialized an alias carrying
 * another value: resolving reads the canonical name first, so emitting it keeps the url and the data
 * naming the same thing. A value given only under an alias is emitted under the canonical name
 * unless the route serialized that alias itself.
 */
function valueToEmit(
    givenValues: Readonly<Record<string, string>>,
    canonical: string,
    aliases: ReadonlySet<string>,
    emittedNames: ReadonlySet<string>,
) {
    if (emittedNames.has(canonical)) {
        return undefined;
    }

    const canonicalValue = readGivenValue(givenValues, canonical);
    if (canonicalValue !== undefined) {
        return canonicalValue;
    }

    for (const alias of aliases) {
        const aliasValue = readGivenValue(givenValues, alias);
        if (aliasValue !== undefined) {
            return emittedNames.has(alias) ? undefined : aliasValue;
        }
    }

    return undefined;
}

/**
 * The registered parameters given in generation data which a route has to serialize after the query
 * of its own template, as name-value pairs under canonical names.
 * @param givenValues Values the url is generated from, cast to strings the way `Route.generateUrl`
 *        casts them.
 * @param emittedNames Names of the query parameters the route serialized already, from its template or a loose query.
 */
export function globalQueryPairsToEmit(givenValues: Readonly<Record<string, string>>, emittedNames: ReadonlySet<string>): [string, string][] {
    const pairs: [string, string][] = [];
    globalQueryParams.forEach((aliases, canonical) => {
        const value = valueToEmit(givenValues, canonical, aliases, emittedNames);
        if (value !== undefined) {
            pairs.push([canonical, value]);
        }
    });

    return pairs;
}
